import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase, ButtonUI, ConfirmDialogUI } from '../Interface';
import { SimpleTaskScheduleFields } from './SimpleTaskScheduleFields';
import { SimpleTaskScheduleReports } from './SimpleTaskScheduleReports';
import dayjs from 'dayjs';
import simpleTaskScheduleService from '../../service/simpleTaskSchedule.service';
import { UnitEnum, UnitsWithFrequency } from '../../config/simpleTaskSchedule';
import { useLoader } from '../../provider/LoaderProvider';

export const SimpleTaskSchedulePage = () => {
  const { showLoader, hideLoader } = useLoader();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const defConfirmDialogData = {
    open: false,
    message: '',
    handleChangeElement: () => {},
  };
  const [confirmDialogData, setConfirmDialogData] = useState(defConfirmDialogData);

  const def = {
    name: '',
    startDate: dayjs(),
    endDate: dayjs(),
    fromTime: dayjs('08:00', 'HH:mm'),
    toTime: dayjs('09:00', 'HH:mm'),
    unit: '',
    weekday: null,
    monthDay: null,
    every: null,
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    setDefaultValues(def);
    setDefaultSelectedValues(def);
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('simpleTaskSchedule');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    startDate: Yup.string().required(),
    endDate: Yup.string().required(),
    fromTime: Yup.string().required(),
    toTime: Yup.string().required(),
    unit: Yup.string().required(),
    every: Yup.number()
      .nullable()
      .when('unit', ([unit], schema) => (UnitsWithFrequency.includes(unit) ? schema.required().min(1) : schema)),
    weekday: Yup.number()
      .nullable()
      .when('unit', (unit, schema) => (unit === UnitEnum.WEEK ? schema.required() : schema)),
    monthDay: Yup.number()
      .nullable()
      .when('unit', (unit, schema) => (unit === UnitEnum.MONTH ? schema.required() : schema)),
  });

  const valuesToForm = () => {
    const { id, name, startDate, endDate, fromTime, toTime, unit, weekday, monthDay, every } = selectedValues;

    setInitValues({
      id,
      name,
      startDate: dayjs(startDate),
      endDate: dayjs(endDate),
      fromTime: dayjs(fromTime),
      toTime: dayjs(toTime),
      unit,
      weekday,
      monthDay,
      every,
    });
  };

  const handleInsertObject = async (values) => {
    const insertObj = {
      ...values,
      every: parseInt(values.every),
    };
    simpleTaskScheduleService
      .createItem(insertObj)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleGenerateTasks = () => {
    showLoader();
    simpleTaskScheduleService
      .generateTasks(selectedValues.id)
      .then(() => {
        addNotification({
          content: 'Sikeres generálás!',
          type: NotificationType.SUCCESS,
        });
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleRemoveElement = async () => {
    simpleTaskScheduleService.deleteItem(selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      onCloseConfirmDialog();
      setSelectedValues(defaultSelectedValues);
    });
  };

  const onCloseConfirmDialog = () => {
    setConfirmDialogData(defConfirmDialogData);
  };

  return (
    <>
      <FormBase
        name="SimpleTaskSchedule"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <div className="flex items-center">
                <FormHeaderUI
                  addClick={() => {
                    setSelectedValues(defaultSelectedValues);
                    setViewField(false);
                    setAddField(false);
                  }}
                  addDisabled={!viewField || !canEdit}
                  deleteClick={() => {
                    setConfirmDialogData({
                      open: true,
                      message: `Biztosan kitörli a ${selectedValues.name} rendszeres teendőt? Az ehhez kapcsolódó feladatok is törlése kerülnek!`,
                      handleChangeElement: () => {
                        handleRemoveElement();
                      },
                    });
                  }}
                  deleteDisabled={operatorsDisabled || !canEdit}
                />
                <ButtonUI
                  text="Generálás"
                  className="bg-warning"
                  onClick={handleGenerateTasks}
                  disabled={!canEdit || !selectedValues?.id}
                />
              </div>

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleInsertObject}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <SimpleTaskScheduleFields
                      viewField={viewField}
                      selectedValues={selectedValues}
                      addField={addField}
                    />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <SimpleTaskScheduleReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
          />
        }
      />
      <ConfirmDialogUI
        handleChangeElement={confirmDialogData.handleChangeElement}
        show={confirmDialogData.open}
        onHide={() => onCloseConfirmDialog()}
        message={confirmDialogData.message}
      />
    </>
  );
};
